export type AnalyticsTransportHealth = "unknown" | "healthy" | "unhealthy";

export type AnalyticsRuntimeSnapshot = {
  runtimeInitialized: boolean;
  adsConfigured: boolean;
  adsTagId?: string;
  dispatchCount: number;
  collectAttemptCount: number;
  transportHealth: AnalyticsTransportHealth;
  transportReason?: string;
  lastDispatchEventName?: string;
  lastDispatchAt?: number;
  lastCollectAt?: number;
};

const COLLECT_URL_REGEX =
  /(google-analytics\.com\/g\/collect|analytics\.google\.com\/g\/collect|\/collect\?v=2)/i;
const TRANSPORT_UNHEALTHY_DISPATCH_THRESHOLD = 4;

let runtimeInitialized = false;
let adsConfigured = false;
let adsTagId: string | undefined;
let dispatchCount = 0;
let collectAttemptCount = 0;
let lastDispatchEventName: string | undefined;
let lastDispatchAt: number | undefined;
let lastCollectAt: number | undefined;
let transportHealth: AnalyticsTransportHealth = "unknown";
let transportReason: string | undefined;
let debugHooksInstalled = false;
let debugSamplingDecided = false;
let debugSamplingEnabled = false;

function hasCollectInUrl(url: string): boolean {
  return COLLECT_URL_REGEX.test(url);
}

function scanCollectAttemptsFromPerformance(): void {
  if (typeof window === "undefined" || typeof performance === "undefined") {
    return;
  }
  try {
    const entries = performance.getEntriesByType(
      "resource"
    ) as PerformanceResourceTiming[];
    let count = 0;
    let mostRecentCollectAt: number | undefined;
    for (const entry of entries) {
      if (hasCollectInUrl(entry.name)) {
        count += 1;
        if (entry.responseEnd > 0) {
          mostRecentCollectAt = Math.max(
            mostRecentCollectAt ?? 0,
            performance.timeOrigin + entry.responseEnd
          );
        }
      }
    }
    collectAttemptCount = Math.max(collectAttemptCount, count);
    if (mostRecentCollectAt) {
      lastCollectAt = mostRecentCollectAt;
    }
  } catch {
    // Ignore performance API issues in older browsers.
  }
}

function recomputeTransportHealth(): void {
  if (collectAttemptCount > 0) {
    transportHealth = "healthy";
    transportReason = undefined;
    return;
  }

  if (runtimeInitialized && dispatchCount >= TRANSPORT_UNHEALTHY_DISPATCH_THRESHOLD) {
    transportHealth = "unhealthy";
    transportReason = "no collect attempts observed after multiple dispatched events";
    return;
  }

  transportHealth = "unknown";
  transportReason = undefined;
}

function updateWindowDebugSnapshot(): void {
  if (typeof window === "undefined") {
    return;
  }
  const debugWindow = window as Window & {
    __kfaAnalyticsDebugSnapshot?: AnalyticsRuntimeSnapshot;
  };
  debugWindow.__kfaAnalyticsDebugSnapshot = getAnalyticsRuntimeSnapshot();
}

function recordCollectUrl(url: string): void {
  if (!hasCollectInUrl(url)) {
    return;
  }
  collectAttemptCount += 1;
  lastCollectAt = Date.now();
  recomputeTransportHealth();
  updateWindowDebugSnapshot();
}

function shouldEnableDebugHooks(): boolean {
  if (debugSamplingDecided) {
    return debugSamplingEnabled;
  }

  debugSamplingDecided = true;
  const enabledByEnv = import.meta.env.VITE_ANALYTICS_DEBUG_TRANSPORT === "true";
  if (!enabledByEnv) {
    debugSamplingEnabled = false;
    return false;
  }

  const sampleRateRaw = import.meta.env.VITE_ANALYTICS_DEBUG_TRANSPORT_SAMPLE_RATE;
  const sampleRate = Number.parseFloat(sampleRateRaw ?? "1");
  if (!Number.isFinite(sampleRate) || sampleRate <= 0) {
    debugSamplingEnabled = false;
    return false;
  }

  debugSamplingEnabled = Math.random() <= Math.min(1, sampleRate);
  return debugSamplingEnabled;
}

export function initializeAnalyticsTransportDebugHooks(): void {
  if (typeof window === "undefined" || debugHooksInstalled || !shouldEnableDebugHooks()) {
    return;
  }

  debugHooksInstalled = true;
  const debugWindow = window as Window & {
    __kfaAnalyticsDebugSnapshot?: AnalyticsRuntimeSnapshot;
    __kfaAnalyticsDebug?: {
      getSnapshot: () => AnalyticsRuntimeSnapshot;
    };
  };

  const originalSendBeacon = navigator.sendBeacon?.bind(navigator);
  if (originalSendBeacon) {
    navigator.sendBeacon = (url: string | URL, data?: BodyInit | null) => {
      recordCollectUrl(String(url));
      return originalSendBeacon(url, data);
    };
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const response = await originalFetch(...args);
    const url = String(args[0]);
    recordCollectUrl(url);
    return response;
  };

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ): void {
    const xhr = this as XMLHttpRequest & { __kfaDebugUrl?: string };
    xhr.__kfaDebugUrl = String(url);
    originalXhrOpen.call(
      this,
      method,
      url.toString(),
      async ?? true,
      username,
      password
    );
  };

  const originalXhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function (
    body?: Document | XMLHttpRequestBodyInit | null
  ): void {
    const xhr = this as XMLHttpRequest & { __kfaDebugUrl?: string };
    if (xhr.__kfaDebugUrl) {
      recordCollectUrl(xhr.__kfaDebugUrl);
    }
    originalXhrSend.call(this, body);
  };

  debugWindow.__kfaAnalyticsDebug = {
    getSnapshot: () => getAnalyticsRuntimeSnapshot(),
  };
  updateWindowDebugSnapshot();
}

export function markAnalyticsRuntimeInitialized(): void {
  runtimeInitialized = true;
  scanCollectAttemptsFromPerformance();
  recomputeTransportHealth();
  updateWindowDebugSnapshot();
}

export function markGoogleAdsConfigured(tagId: string): void {
  adsConfigured = true;
  adsTagId = tagId;
  updateWindowDebugSnapshot();
}

export function recordAnalyticsDispatchAttempt(eventName: string): void {
  dispatchCount += 1;
  lastDispatchEventName = eventName;
  lastDispatchAt = Date.now();
  scanCollectAttemptsFromPerformance();
  recomputeTransportHealth();
  updateWindowDebugSnapshot();
}

export function getAnalyticsRuntimeSnapshot(): AnalyticsRuntimeSnapshot {
  scanCollectAttemptsFromPerformance();
  recomputeTransportHealth();
  return {
    runtimeInitialized,
    adsConfigured,
    adsTagId,
    dispatchCount,
    collectAttemptCount,
    transportHealth,
    transportReason,
    lastDispatchEventName,
    lastDispatchAt,
    lastCollectAt,
  };
}
